// Pulse / Money section (redesign hero)
//
// The lead surface of Pulse, because cash leads. Full-width hero: left =
// paid-this-month headline + gradient underline + outstanding / overdue /
// avg-ticket; right = the most recent succeeded payments. Restyled onto
// the console kit (Surface + tokens); data contract unchanged
// (MoneyPulse from /v2/console/pulse/money).

import React, { useContext } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SectionShell from './SectionShell';
import { Surface, useTokens } from '../../../components/primitives';
import { fmtUsd, fmtUsdPrecise } from '../demoData';
import { BusinessContext } from '../../../contexts/BusinessContext';

function formatRelative(iso) {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function SubMetric({ value, label, color, labelColor }) {
  return (
    <Box>
      <Typography
        sx={{ fontSize: 18, fontWeight: 800, color, letterSpacing: '-0.4px' }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: labelColor }}>
        {label}
      </Typography>
    </Box>
  );
}

export default function MoneySection({ money }) {
  const t = useTokens();
  const { business } = useContext(BusinessContext);
  const navigate = useNavigate();
  const basePath = business?.slug ? `/business/${business.slug}` : '';

  if (!money) {
    return (
      <SectionShell
        title="Money"
        empty
        emptyCopy="Once your first invoice gets paid, this is where you'll see your cash flow."
      />
    );
  }

  const deltaPct = money.paidPriorMonthCents
    ? Math.round(
        ((money.paidThisMonthCents - money.paidPriorMonthCents) /
          money.paidPriorMonthCents) *
          100
      )
    : 0;

  return (
    <Surface
      radius="16px"
      sx={{
        p: { xs: 2.25, sm: '20px 22px' },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2.5, md: 3.25 },
      }}
    >
      {/* Left: headline */}
      <Box
        sx={{
          flex: { md: 1.1 },
          minWidth: 0,
          borderRight: { md: `1px solid ${t.hairline}` },
          pr: { md: 3.25 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '.8px',
              textTransform: 'uppercase',
              color: t.faint,
            }}
          >
            Money
          </Typography>
          <Typography sx={{ fontSize: 11, color: t.faint }}>
            · paid this month
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography
            sx={{
              fontSize: { xs: 32, sm: 40 },
              fontWeight: 900,
              letterSpacing: '-1.5px',
            }}
          >
            {fmtUsd(money.paidThisMonthCents)}
          </Typography>
          {deltaPct !== 0 ? (
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: deltaPct > 0 ? t.green : t.red,
              }}
            >
              {deltaPct > 0 ? '▲' : '▼'} {Math.abs(deltaPct)}%
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            height: 4,
            width: 70,
            borderRadius: '4px',
            background: t.gradient,
            m: '8px 0 18px',
          }}
        />
        <Box sx={{ display: 'flex', gap: 2.75, flexWrap: 'wrap' }}>
          <SubMetric
            value={fmtUsd(money.outstandingCents)}
            label={`Outstanding · ${money.outstandingCount}`}
            color={t.text}
            labelColor={t.faint}
          />
          <SubMetric
            value={fmtUsd(money.overdueCents)}
            label={`Overdue · ${money.overdueCount}`}
            color={money.overdueCents > 0 ? t.red : t.text}
            labelColor={money.overdueCents > 0 ? t.red : t.faint}
          />
          <SubMetric
            value={fmtUsd(money.avgTicketCents)}
            label="Avg ticket"
            color={t.text}
            labelColor={t.faint}
          />
        </Box>
      </Box>

      {/* Right: recent payments */}
      <Box sx={{ flex: { md: 1 }, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '.8px',
            textTransform: 'uppercase',
            color: t.faint,
            mb: 1.25,
          }}
        >
          Recent payments
        </Typography>
        {money.recent.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: t.muted }}>
            No payments yet this period.
          </Typography>
        ) : (
          money.recent.slice(0, 4).map((p, idx) => (
            <Box
              key={p.id}
              onClick={
                p.invoiceId
                  ? () => navigate(`${basePath}/invoices/${p.invoiceId}`)
                  : undefined
              }
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                py: 0.75,
                borderTop: idx === 0 ? 'none' : `1px solid ${t.hairline}`,
                cursor: p.invoiceId ? 'pointer' : 'default',
                '&:hover': p.invoiceId ? { bgcolor: t.subtle } : undefined,
                mx: p.invoiceId ? -0.5 : 0,
                px: p.invoiceId ? 0.5 : 0,
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '7px',
                  bgcolor: t.greenBg,
                  color: t.green,
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 12,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                $
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  noWrap
                  sx={{ fontSize: 12.5, fontWeight: 700, color: t.text }}
                >
                  {p.customerName}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: t.faint }}>
                  {p.invoiceNumber} · {(p.method || '').toUpperCase()} ·{' '}
                  {formatRelative(p.paidAt)}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: t.text,
                  whiteSpace: 'nowrap',
                }}
              >
                {fmtUsdPrecise(p.amountCents)}
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Surface>
  );
}
